//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  RIGHT_STATUSES_READ_ONLY_FORM,
  TIKTOK_RIGHTS_REQUEST_COPY_CHARACTER_LIMIT,
} from '@uia/rights/constants/uiaRightsConstants';
import { useUIARightsRequestContext } from '@uia/rights/context/UIARightsRequestContext';
import { ExtendedRightsRequest } from '@uia/rights/forms/ExtendedRightsRequest';
import { RequestRequiredForMissingTCUrl } from '@uia/rights/forms/RequestRequiredForMissingTCUrl';
import { useGenerativeAiRightsRequestQuery } from '@uia/rights/hooks/useGenerativeAiRightsRequestQuery';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { useRequestRightsInAuthorizedUrl } from '@uia/rights/hooks/useRequestRightsInAuthorizedUrl';
import { getRequestMetadata } from '@uia/rights/utils/rightsRequestUtils';
import { copyTextToClipboard } from '@utils/copyToClipboard';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface TikTokRightsRequestProps {
  approvalPhrase: string | undefined;
  cuid: Cuid;
  isReRequestRequiredForMissingTCUrl: boolean;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  setRightsRequestText: (value: string) => void;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const TikTokRightsRequest = ({
  approvalPhrase,
  cuid,
  isReRequestRequiredForMissingTCUrl,
  requestType,
  rightsRequestText,
  setRightsRequestText: setRightsRequestText,
  setSelectedOption,
}: TikTokRightsRequestProps) => {
  const requestRightsInSocialNetworkUrl = useRequestRightsInAuthorizedUrl();

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: hasFormErrors,
    isError: isErrorFormErrors,
    isLoading: isLoadingFormErrors,
  } = useHasRightsFormErrorsQuery(rightsRequestText);

  const { trackEvent } = useMediaTrackingContext();

  const { requestTextIndex, requestTextSource, selectedRightsOption } =
    useUIARightsRequestContext();

  const { data: aiGeneratedMessage } = useGenerativeAiRightsRequestQuery({
    approvalPhrase,
    cuid,
    rightsRequestText,
  });

  if (
    isLoadingMetadata ||
    isErrorMetadata ||
    isLoadingFormErrors ||
    isErrorFormErrors
  ) {
    return null;
  }

  const {
    networkDMUrl,
    originalUrl,
    rightsRequestStatus: {
      approvalPhrase: previousApprovalPhrase,
      communicationType,
      status,
    },
  } = metadata;

  const onClickCopyPaste = async () => {
    if (!rightsRequestText) {
      return;
    }

    trackEvent({
      cuid,
      event: MediaTrackingEventName.RightsRequested,
    });

    const targetUrl =
      requestType === RightsRequestOptions.COMMENTS
        ? originalUrl
        : networkDMUrl;

    await copyTextToClipboard(rightsRequestText);

    const metadata = getRequestMetadata(
      rightsRequestText,
      requestTextIndex,
      requestTextSource,
      aiGeneratedMessage,
      selectedRightsOption,
    );

    return requestRightsInSocialNetworkUrl({
      approvalPhrase,
      communicationType: RightsRequestCommunicationType.COMMENT,
      cuid,
      requestMessage: rightsRequestText,
      targetUrl,
      requestMessageSource: metadata.requestMessageSource,
      requestMessageLanguage: metadata.requestMessageLanguage,
    });
  };

  const isReadOnlyForm = RIGHT_STATUSES_READ_ONLY_FORM.includes(status);

  const isOverCharacterLimit = rightsRequestText
    ? rightsRequestText.length > TIKTOK_RIGHTS_REQUEST_COPY_CHARACTER_LIMIT
    : false;
  const shouldDisableButton = hasFormErrors || isOverCharacterLimit;

  return (
    <Wrapper>
      {status !== RIGHTS_STATUS.EXPIRED ? (
        <>
          {!isReRequestRequiredForMissingTCUrl && !isReadOnlyForm && (
            <FlexStart>
              <RequiredPermissionsButton
                isDisabled={shouldDisableButton}
                onClick={onClickCopyPaste}
                permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
                trackingId={
                  UIATrackingIds.RIGHTS_REQUEST_TIKTOK_COMMENT_REQUEST_BUTTON
                }
              >
                Copy and Paste in TikTok
              </RequiredPermissionsButton>
              <>
                <CaptionSecondaryText>
                  This will open TikTok in a new window. Make sure you're logged
                  into the correct TikTok account before posting the comment.
                </CaptionSecondaryText>
                <PrimaryAnchor
                  href={MANAGE_RIGHTS_REQUEST_ARTICLE_URL}
                  target='_blank'
                  trackingId={
                    UIATrackingIds.RIGHTS_REQUEST_MANAGE_RIGHTS_LEARN_MORE_LINK
                  }
                >
                  Learn more
                </PrimaryAnchor>
              </>
            </FlexStart>
          )}

          <RequestRequiredForMissingTCUrl
            cuid={cuid}
            isReRequestRequiredForMissingTCUrl={
              isReRequestRequiredForMissingTCUrl
            }
            requestType={requestType}
            setRightsRequestText={setRightsRequestText}
            setSelectedOption={setSelectedOption}
          />
        </>
      ) : (
        <ExtendedRightsRequest
          communicationType={communicationType}
          cuid={cuid}
          previousApprovalPhrase={previousApprovalPhrase}
          rightsRequestText={rightsRequestText}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]} 0;
  `,
);

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
